/**
 * Le journal complet (SPEC-UI §3.6) — panneau posé sur la chambre (⟦A-7⟧ décision 4).
 * Il porte aussi l'escalier à découvert : ce qui est ouvert, et combien il reste de portes.
 *
 * ## Les filtres ⟦SPEC-UI §3.6 · A-70 §5 · A-81⟧
 *
 * Le journal garde 80 lignes et une partie en produit des centaines : en fin de course, tout
 * ce qui compte est noyé sous la trame des jours. Le croquis de la spec propose
 * `Tous | Buzz | ₵ | Technique`.
 *
 * ⟦A-70⟧ les avait livrés **par icône**, faute de mieux : une entrée de journal était un
 * `{ texte, ton, icône }` et la majorité des lignes venait du `log` du moteur, sans type ni
 * catégorie — « Technique » n'avait aucun équivalent, le correctif était « un champ de famille
 * sur `LogEntry`, c'est le moteur ». ⟦A-81⟧ l'a posé : chaque émetteur du moteur écrit sa
 * famille (`LogEntry.famille`), chaque fait traduit choisit la sienne (`game/journal.ts`), et
 * cet écran ne fait plus que lire une donnée. La table par icône ne survit que pour les entrées
 * persistées avant l'avenant (`familleDeEntree`). S'y ajoute toujours la **recherche en texte
 * libre**, insensible aux accents — l'outil le plus efficace sur 80 lignes.
 *
 * ## ⟦C57 · lot 1 · A-174⟧ Les dessous des faits
 *
 * Une ligne reste COURTE (icône, jour, une phrase). Quand le fait qu'elle raconte a un détail
 * (`JournalEntry.detail`, écrit par `game/journal.ts`), la ligne porte le pictogramme 📰 « Les
 * dessous » au bout : il rouvre le détail dans le dialogue partagé, aussi tard qu'on veut tant que
 * l'entrée est gardée. Le Journal est l'archive de ce que les autres écrans n'affichent plus ;
 * la recherche lit donc aussi dans ces détails, sans quoi ce qui a quitté l'écran serait
 * introuvable.
 *
 * ## ⟦C57 · lot 5d · A-174⟧ Les lignes sans détail, et l'escalier
 *
 * Deux restes de texte à l'écran. (1) Les lignes du `log` du moteur n'ont pas de `detail` et
 * peuvent faire trois phrases : `ligneCourte` n'en montre que la première, et le pictogramme 📰 rouvre
 * le reste (le texte de l'entrée reste intact dans la run : on coupe à l'affichage, jamais dans les
 * données). (2) « Ce que ta chambre a gagné » listait neuf portes avec leur condition : une rangée de
 * pictogrammes et un compteur, la liste entière derrière « Les dessous ».
 */

import { useMemo, useState } from "react";

import { DessousDuJournal } from "../../components/DessousDuJournal";
import { DessousLignes, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { ESCALIER, ouvert } from "../../game/escalier";
import type { DetailJournal, JournalEntry } from "../../game/gameClient";
import {
  FAMILLES,
  detailJournal,
  familleDeEntree,
  texteDuDetail,
  type FamilleFiltre,
} from "../../game/journal";
import { useRunStore } from "../../stores/runStore";

import { ligneCourte } from "./ligne-courte";

function familleDe(entree: JournalEntry): FamilleFiltre {
  return familleDeEntree(entree);
}

/** Comparaison tolérante aux accents et à la casse : on cherche « reputation », on trouve « Réputation ». */
function normaliser(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Ce que la ligne affiche et ce que le pictogramme ouvre. Une entrée à `detail` (lot 1) garde sa
 * ligne et son détail tels quels ; une entrée sans détail dont le texte dépasse la règle est coupée
 * à l'affichage, et ce qui part est rangé dans un détail fabriqué à la volée (jamais écrit dans la run).
 */
function ligneEtDetail(entree: JournalEntry): { texte: string; detail: DetailJournal | undefined } {
  if (entree.detail) return { texte: entree.texte, detail: entree.detail };
  const court = ligneCourte(entree.texte);
  if (court.reste === null) return { texte: entree.texte, detail: undefined };
  return {
    texte: court.courte,
    detail: detailJournal(court.courte, [{ paragraphes: [court.reste] }]),
  };
}

export function Journal() {
  const run = useRunStore((s) => s.run);
  const [famille, setFamille] = useState<FamilleFiltre>("tout");
  const [recherche, setRecherche] = useState("");

  const journal = run?.journal;
  const lignes = useMemo(() => {
    if (!journal) return [];
    const q = normaliser(recherche.trim());
    return [...journal]
      .reverse()
      .filter((e) => famille === "tout" || familleDe(e) === famille)
      .filter(
        (e) =>
          q.length === 0 ||
          normaliser(e.texte).includes(q) ||
          // ⟦C57 · lot 1⟧ Le détail aussi : c'est là que sont passés les mots que la ligne ne dit plus.
          (e.detail !== undefined && normaliser(texteDuDetail(e.detail)).includes(q)),
      );
  }, [journal, famille, recherche]);

  /**
   * Le compte par famille, sur le journal **entier** : un onglet vide doit se voir avant d'être
   * cliqué. Un filtre qui mène à une page blanche fait douter de l'écran, pas du filtre.
   */
  const comptes = useMemo(() => {
    const c = new Map<FamilleFiltre, number>();
    for (const e of journal ?? []) {
      const f = familleDe(e);
      c.set(f, (c.get(f) ?? 0) + 1);
    }
    return c;
  }, [journal]);

  if (!run) return null;

  return (
    <Panneau titre="📰 Journal">
      {/* ⟦C57 · lot 1⟧ Le titre tient en 4 mots ; le compteur, qui était DANS le titre (7 mots), le
          suit à côté. */}
      <div className="journal-entete">
        <h2 className="bloc-titre">Ce qui est arrivé</h2>
        <span className="journal-compteur">{run.journal.length} entrées gardées</span>
      </div>

      {/* ⟦SPEC-UI §3.6⟧ Les filtres. `aria-pressed` plutôt qu'un `role="tablist"` : ce ne sont
          pas des onglets qui changent de page, c'est un tamis qu'on pose sur la même liste. */}
      <div className="journal-filtres" role="group" aria-label="Filtrer le journal">
        {FAMILLES.map((f) => {
          const combien = f.id === "tout" ? run.journal.length : (comptes.get(f.id) ?? 0);
          return (
            <button
              key={f.id}
              type="button"
              className={`puce-filtre ${famille === f.id ? "active" : ""}`}
              aria-pressed={famille === f.id}
              // Un tamis vide se montre éteint plutôt que de se cacher : ce qui disparaît d'un
              // écran à l'autre force à chercher où c'est passé ⟦A-49⟧.
              disabled={combien === 0}
              onClick={() => setFamille(f.id)}
            >
              {f.libelle} <span className="puce-compte">{combien}</span>
            </button>
          );
        })}
        <input
          type="search"
          className="journal-recherche"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Chercher un mot…"
          aria-label="Chercher dans le journal"
        />
      </div>

      {lignes.length === 0 ? (
        <p className="vide">
          {recherche.trim().length > 0
            ? `Rien qui parle de « ${recherche.trim()} » dans ce qui est gardé.`
            : "Rien dans cette famille pour l'instant."}
        </p>
      ) : (
        <ul className="journal">
          {lignes.map((entree) => {
            const { texte, detail } = ligneEtDetail(entree);
            return (
              <li
                className={`journal-ligne ton-${entree.ton}${detail ? " journal-ligne-detail" : ""}`}
                key={entree.id}
              >
                <span aria-hidden="true">{entree.icone}</span>
                <span>
                  <span className="journal-jour">J{entree.jour}</span>
                  {texte}
                </span>
                {/* ⟦C57 · lot 1⟧ Le détail du fait, rouvrable plus tard : le pictogramme au bout de la
                    ligne. ⟦lot 5d⟧ Aussi pour une ligne sans détail que sa longueur fait couper. */}
                {detail ? <DessousDuJournal detail={detail} className="journal-dessous" /> : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* ⟦A-5 + A-7⟧ L'escalier, à découvert : ce qui est ouvert et l'objet que ça a mis dans
          la chambre — sans jamais dire ce que fera une mécanique encore fermée. */}
      {/* ⟦C57 · lot 5d⟧ Une rangée de pictogrammes et un compteur ; la liste d'avant (l'objet que chaque
          porte a mis dans la chambre, et sa condition) est derrière « Les dessous ». */}
      <div className="journal-entete journal-entete-escalier">
        <h2 className="bloc-titre bloc-titre-espace">Ta chambre a gagné</h2>
        <span className="journal-compteur">
          {ESCALIER.filter((e) => ouvert(run.state, e.id)).length} / {ESCALIER.length}
        </span>
        <LesDessous titre="Ce que ta chambre a gagné">
          <DessousLignes
            lignes={ESCALIER.map((etage) => {
              const fait = ouvert(run.state, etage.id);
              return {
                icone: fait ? etage.icone : "🔒",
                texte: (
                  <>
                    {fait ? etage.objet : "…"} — {etage.condition}
                  </>
                ),
              };
            })}
          />
        </LesDessous>
      </div>
      <ul className="escalier-icones" aria-label="Les portes ouvertes de la chambre">
        {ESCALIER.map((etage) => {
          const fait = ouvert(run.state, etage.id);
          return (
            <li
              className={`escalier-icone ${fait ? "ouverte" : ""}`}
              key={etage.id}
              title={fait ? etage.objet : etage.condition}
            >
              <span role="img" aria-label={fait ? etage.objet : `Fermé : ${etage.condition}`}>
                {fait ? etage.icone : "🔒"}
              </span>
            </li>
          );
        })}
      </ul>
    </Panneau>
  );
}
